import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { INFO, REVIEWS, REVIEW_TAGS } from "@/data/content";

const Stars = ({ className = "size-3.5" }) => (
  <span className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${className} fill-gold text-gold`} />
    ))}
  </span>
);

export const Recensioni = () => {
  const [tag, setTag] = useState("tutte");
  const visible =
    tag === "tutte" ? REVIEWS : REVIEWS.filter((r) => r.tags.includes(tag));

  return (
    <section
      id="recensioni"
      className="scroll-mt-20 border-y border-sand/10 bg-roast py-20 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <Reveal>
            <Eyebrow>Recensioni</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Cosa dicono i clienti
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-5">
              <p className="font-serif text-6xl font-semibold text-cream">
                {INFO.rating}
              </p>
              <div>
                <div className="relative inline-flex">
                  <span className="flex gap-0.5 text-sand/25">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="size-5" />
                    ))}
                  </span>
                  <span
                    className="absolute inset-0 flex gap-0.5 overflow-hidden"
                    style={{ width: "86%" }}
                  >
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="size-5 shrink-0 fill-gold text-gold" />
                    ))}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-cream/60">
                  {INFO.reviewCount} recensioni su Google
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap gap-2">
            {[{ id: "tutte", label: "Tutte" }, ...REVIEW_TAGS.map((t) => ({ id: t.id, label: `${t.id} · ${t.count}` }))].map(
              (t) => (
                <button
                  key={t.id}
                  data-testid={`review-filter-tag-${t.id}`}
                  onClick={() => setTag(t.id)}
                  className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                    tag === t.id
                      ? "border-terra bg-terra text-cream"
                      : "border-sand/20 text-cream/65 hover:border-sand/45 hover:text-cream"
                  }`}
                >
                  {t.label}
                </button>
              )
            )}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((r, i) => (
              <motion.article
                key={`${r.name}-${r.text.slice(0, 12)}`}
                layout
                data-testid={`review-card-${i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col justify-between rounded-2xl border border-sand/15 bg-mocha/60 p-7"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <Stars />
                    {r.highlight && (
                      <span className="rounded-full border border-sand/25 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-sand">
                        In evidenza
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-cream/85">
                    "{r.text}"
                  </p>
                  {r.extra && (
                    <p className="mt-2 text-sm text-cream/55">{r.extra}</p>
                  )}
                </div>
                <footer className="mt-6 border-t border-sand/10 pt-4">
                  <p className="text-sm font-semibold text-cream">{r.name}</p>
                  <p className="text-xs text-cream/50">
                    {[r.meta, r.when].filter(Boolean).join(" · ")}
                  </p>
                </footer>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
